import { useUserQuery } from "../../hooks/queries/useUserQuery";
import { Mode } from "../../types/tutor.types";
import { useChatHistoryQuery } from "../../hooks/queries/useChatHistoryQuery";
import { useSuggestionsQuery } from "../../hooks/queries/useSuggestionsQuery";
import { useGroupListStore } from "../../store/useGroupListStore";
import { useTutorChatStore } from "../../store/useTutorChatStore";
import { useMediaMd } from "../../../../hooks/useMedia";
import { useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import useChat from "../useChat";
import { useAuth } from "../useAuth";

const useTutorV2View = () => {
  const isMd = useMediaMd();
  const { isLoggedIn } = useUserQuery();
  const { id } = useParams<{ id?: string }>();
  const { currentGroupId, setCurrentGroupId } = useGroupListStore();
  const { submitMessage } = useChat(Mode.TUTOR);
  const { checkLoginBeforeAction, showLoginModal } = useAuth();

  // Suggestion Cards 조회 (동적 API 페칭, 에러 시 정적 fallback)
  const { data: suggestions = [] } = useSuggestionsQuery(Mode.TUTOR);

  // 채팅 히스토리 조회 (empty state 판단용)
  const { data: chatHistory } = useChatHistoryQuery({
    mode: Mode.TUTOR,
    groupId: currentGroupId,
  });

  // 스트리밍 중인 메시지 확인 (Zustand)
  const { history: streamingHistory } = useTutorChatStore();

  // 백엔드 또는 스트리밍 메시지 중 하나라도 있으면 ChatContainer 표시
  // ChatContainer와 동일한 필터링 로직 적용 (깜빡임 방지)
  const hasAnyMessages = useMemo(() => {
    const chatHistoryCount = chatHistory?.length || 0;

    // ChatContainer와 동일한 스트리밍 메시지 필터링
    const backendMessageIds = new Set(chatHistory?.map((msg) => msg.id) || []);
    const filteredStreamingMessages = streamingHistory.filter((msg) => {
      // 백엔드에 이미 저장된 메시지는 제외
      if (backendMessageIds.has(msg.id)) return false;

      // 현재 그룹의 메시지만 표시 (타입 변환 고려)
      if (
        msg.group_id === currentGroupId ||
        msg.group_id === String(currentGroupId) ||
        String(msg.group_id) === currentGroupId
      )
        return true;

      // currentGroupId가 없는 경우 (첫 질문): 임시 그룹 ID (temp-로 시작) 메시지 표시
      if (!currentGroupId && msg.group_id?.toString().startsWith("temp-"))
        return true;

      return false;
    });

    const result = chatHistoryCount > 0 || filteredStreamingMessages.length > 0;
    return result;
  }, [chatHistory, streamingHistory, currentGroupId]);

  /**
   * URL 파라미터로 그룹 선택 (레거시 동작 재현)
   *
   * 레거시 Tutor.js (lines 938-958):
   * - location.pathname.split("/")[2]로 그룹 ID 추출
   * - getHistory()로 해당 그룹의 채팅 히스토리 로드
   *
   * TutorV2:
   * - useParams()로 그룹 ID 추출
   * - setCurrentGroupId()로 Zustand 스토어 업데이트
   * - useChatHistoryQuery가 자동으로 해당 그룹 히스토리 로드
   */
  useEffect(() => {
    if (id && id !== currentGroupId) {
      // URL에 그룹 ID가 있고, 현재 선택된 ID와 다르면 업데이트
      setCurrentGroupId(id);
    } else if (!id && currentGroupId) {
      // URL에 그룹 ID가 없으면 선택 해제
      setCurrentGroupId(null);
    }
  }, [id]);

  const handleCardClick = checkLoginBeforeAction(
    (title: string) => {
      // 카드 클릭 시 Prompt에 텍스트 자동 입력 및 제출
      submitMessage(title);
    },
    // 비로그인 시 모달 표시
    showLoginModal
  );

  return {
    isMd,
    isLoggedIn,
    suggestions,
    hasAnyMessages,
    currentGroupId,
    handleCardClick,
  };
};

export default useTutorV2View;
